import { Blac, Cubit } from "blac-next";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import translate from "src/lib/translate";
import AnnouncementPopupCubit from "src/ui/components/AnnouncementPopup/AnnouncementPopupCubit";
import { TourGuideCubit } from "@9amhealth/shared";
import createChatTour, { CHAT_TOUR_ID, type ChatTourVariant } from "./chatTour";
import { ConversationService } from "./ConversationService";
import hiImSophieImage from "../hi_im_sophie.webp";
import reportErrorSentry from "src/lib/reportErrorSentry";

export interface LeapChatRouterState {
  lastChatPath: string | null;
  autoSelectPath: string | null;
  checking: boolean;
  showAnnouncement: boolean;
  conversationHistoryId: string | undefined;
}

export default class LeapChatRouterCubit extends Cubit<LeapChatRouterState> {
  static keepAlive = true;

  private static readonly INDEX_REDIRECT_KEY = "chatIndexRedirectDone";

  private autoConversationSelectDone = false;

  constructor() {
    super({
      lastChatPath: null,
      autoSelectPath: null,
      checking: true,
      showAnnouncement: false,
      conversationHistoryId: undefined
    });

    this.autoConversationSelectDone =
      StorageController.getItem(LeapChatRouterCubit.INDEX_REDIRECT_KEY) ===
      "true";
  }

  setConversationHistoryId = (id: string | undefined): void => {
    if (id !== this.state.conversationHistoryId) {
      this.patch({ conversationHistoryId: id });
    }
  };

  initTourGuide = (): (() => void) => {
    const appView = Blac.getBloc(AppViewCubit);
    appView.setAllowIonContentScrollY(false);

    const tourGuide = Blac.getBloc(TourGuideCubit);
    tourGuide.setStorage({
      getItem: (key) => StorageController.getItem(key),
      setItem: (key, value) => StorageController.setItem(key, value)
    });

    return () => {
      appView.setAllowIonContentScrollY(true);
    };
  };

  startTourFromSource = (
    variant: ChatTourVariant,
    navigate: (path: string) => void
  ): void => {
    const { conversationHistoryId } = this.state;
    if (!conversationHistoryId) {
      reportErrorSentry(
        new Error("Cannot start chat tour without conversation history ID")
      );
      return;
    }

    const appView = Blac.getBloc(AppViewCubit);
    appView.setAllowIonContentScrollY(false);

    const tourGuide = Blac.getBloc(TourGuideCubit);
    tourGuide.registerTour(
      createChatTour({
        variant,
        navigate,
        conversationHistoryId
      })
    );
    void tourGuide.startTour(CHAT_TOUR_ID, true);
  };

  trackPath = (pathname: string): void => {
    if (pathname.startsWith("/app/chat/")) {
      this.patch({ lastChatPath: pathname });
    }
  };

  selectAutoRedirectConversation = (
    conversations: {
      id: string;
      createdAt: string;
      unreadMessageCount: number;
    }[]
  ): void => {
    if (conversations.length !== 2) {
      return;
    }
    const conversationsSorted = conversations.toSorted(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const conversationWithUnread = conversationsSorted.find(
      (conv) => conv.unreadMessageCount > 0
    );
    if (conversationWithUnread) {
      this.autoConversationSelectDone = true;
      StorageController.setItem(LeapChatRouterCubit.INDEX_REDIRECT_KEY, "true");
      this.patch({
        autoSelectPath: `/app/chat/conversation/${conversationWithUnread.id}?showInfoCard`
      });
    }
  };

  resolveIndexRedirect = (): void => {
    const { conversationHistoryId } = this.state;

    if (!conversationHistoryId || this.autoConversationSelectDone) {
      this.patch({ checking: false });
      return;
    }

    void ConversationService.getInstance()
      .listConversations()
      .then((conversations) => {
        this.selectAutoRedirectConversation(conversations);
        this.checkSophieAnnouncement(conversations, conversationHistoryId);
      })
      .catch(() => {
        // fall through to default behavior
      })
      .finally(() => {
        this.patch({ checking: false });
      });
  };

  private checkSophieAnnouncement = (
    conversations: { id: string }[],
    previousConversationId: string
  ): void => {
    const hasOnlyPreviousConversation =
      conversations.length === 1 &&
      conversations[0].id === previousConversationId;

    if (!hasOnlyPreviousConversation) {
      return;
    }

    this.autoConversationSelectDone = true;
    StorageController.setItem(LeapChatRouterCubit.INDEX_REDIRECT_KEY, "true");

    this.patch({ showAnnouncement: true });
  };

  showSophieAnnouncement = (
    onConfirm: () => void,
    onCancel: () => void
  ): void => {
    if (!this.state.showAnnouncement) {
      return;
    }

    this.patch({ showAnnouncement: false });

    const popup = Blac.getBloc(AnnouncementPopupCubit);
    popup.show({
      title: translate("announcementPopup.sophie.title"),
      body: translate("announcementPopup.sophie.body"),
      confirmText: translate("announcementPopup.sophie.confirmText"),
      cancelText: translate("announcementPopup.sophie.cancelText"),
      image: hiImSophieImage,
      onConfirm,
      onCancel
    });
  };
}
